import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AutocompleteInput } from '@/components/ui/autocomplete-input';
import { useToast } from '@/components/ui/use-toast';
import DateField from './DateField';
import { membershipCardsService } from '../../services/membershipCards';
import { nextExpiryFor } from './membership-expiry';
import type {
  MembershipCard,
  MembershipCardPayload,
  MembershipOrganization,
} from '../../types/membership';

interface MembershipCardFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  gymName: string;
  memberId: number;
  organizations: MembershipOrganization[];
  /** Null when creating. */
  card: MembershipCard | null;
  onSaved: () => void;
}

const emptyForm: MembershipCardPayload = {
  membership_organization_id: null,
  number: '',
  kind: '',
  expires_on: '',
};

const today = new Date();
// A card is annual; allow a few years either side so a lapsed one can still be
// recorded and a renewal entered ahead of time.
const OLDEST = new Date(today.getFullYear() - 5, 0, 1);
const LATEST = new Date(today.getFullYear() + 5, 11, 31);

export default function MembershipCardFormDialog({
  open,
  onOpenChange,
  gymSlug,
  gymName,
  memberId,
  organizations,
  card,
  onSaved,
}: MembershipCardFormDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<MembershipCardPayload>(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setForm(
      card
        ? {
            membership_organization_id: card.organization?.id ?? null,
            number: card.number,
            kind: card.kind ?? '',
            expires_on: card.expires_on ?? '',
          }
        : emptyForm
    );
  }, [open, card]);

  const selected = useMemo(
    () => organizations.find((row) => row.id === form.membership_organization_id) ?? null,
    [organizations, form.membership_organization_id]
  );

  // Picking a body fills in when its cards fall due, because that is a property
  // of the body rather than something staff should have to remember. It stays
  // editable: a card bought last season expires on last season's date.
  const handleOrganizationChange = (value: string) => {
    const organization = organizations.find((row) => String(row.id) === value) ?? null;

    setForm((prev) => ({
      ...prev,
      membership_organization_id: organization?.id ?? null,
      expires_on: nextExpiryFor(organization) ?? prev.expires_on,
    }));
  };

  const fetchKinds = useCallback(
    (prefix: string) => membershipCardsService.kinds(gymSlug, prefix),
    [gymSlug]
  );

  const canSave = form.membership_organization_id !== null && form.number.trim().length > 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave || saving) return;

    setSaving(true);
    try {
      if (card) {
        await membershipCardsService.update(gymSlug, memberId, card.id, form);
      } else {
        await membershipCardsService.create(gymSlug, memberId, form);
      }
      onSaved();
    } catch (error) {
      toast({
        title: card
          ? t('gymAdmin.members.membership.updateError')
          : t('gymAdmin.members.membership.createError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {card
              ? t('gymAdmin.members.membership.editTitle')
              : t('gymAdmin.members.membership.newTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('gymAdmin.members.membership.formDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="card-organization">
              {t('gymAdmin.members.membership.fields.organization')}
            </Label>
            {/* Empty string rather than undefined for "nothing picked yet":
                React would otherwise see the field switch from uncontrolled to
                controlled the moment a body is chosen. */}
            <Select
              value={
                form.membership_organization_id === null
                  ? ''
                  : String(form.membership_organization_id)
              }
              onValueChange={handleOrganizationChange}
            >
              <SelectTrigger id="card-organization">
                <SelectValue
                  placeholder={t('gymAdmin.members.membership.fields.organizationPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((organization) => (
                  <SelectItem key={organization.id} value={String(organization.id)}>
                    {organization.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="card-number">
                {t('gymAdmin.members.membership.fields.number')}
              </Label>
              <Input
                id="card-number"
                value={form.number}
                onChange={(e) => setForm((prev) => ({ ...prev, number: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="card-kind">{t('gymAdmin.members.membership.fields.kind')}</Label>
              <AutocompleteInput
                id="card-kind"
                value={form.kind ?? ''}
                onChange={(value) => setForm((prev) => ({ ...prev, kind: value }))}
                fetchSuggestions={fetchKinds}
                placeholder={t('gymAdmin.members.membership.fields.kindPlaceholder')}
              />
            </div>
          </div>

          <DateField
            id="card-expires-on"
            label={t('gymAdmin.members.membership.fields.expiresOn')}
            value={form.expires_on ?? ''}
            onChange={(value) => setForm((prev) => ({ ...prev, expires_on: value }))}
            captionLayout="dropdown"
            startMonth={OLDEST}
            endMonth={LATEST}
          />

          {/* The issuing gym is this one and cannot be anything else — a card is
              bought through the affiliation of the gym recording it. A national
              body's cards belong to the climber, so they record no gym at all. */}
          {selected && (
            <p className="text-sm text-muted-foreground">
              {selected.global_cards
                ? t('gymAdmin.members.membership.globalCardHint')
                : t('gymAdmin.members.membership.issuedBy', { gym: gymName })}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave || saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
